import { Component, ElementRef } from '@angular/core';
import * as d3 from 'd3';
import { colorPalette } from '../../shared/utils';
import { TooltipOptions } from '../../tooltip/tooltip.component';
import { ChartOptions, MproChart } from '../chart.model';

export class HorizontalGroupedBarChartDataModel {
  constructor(
    public group: string,
    public bars: HorziontalGroupedBarDataModel[]) { }
}

export class HorziontalGroupedBarDataModel {
  constructor(
    public key: string,
    public value: number,
    public tooltip?: TooltipOptions) { }
}

export class HorizontalGroupedBarChartOptions extends ChartOptions {
  dataAxisStep: number;
  tickFormat?: (tick: any) => string
}

@Component({
  selector: 'mpro-horizontal-grouped-bar-chart',
  templateUrl: './horizontal-grouped-bar-chart.component.html',
  styleUrls: ['./horizontal-grouped-bar-chart.component.scss']
})
export class HorizontalGroupedBarChartComponent extends MproChart<HorizontalGroupedBarChartDataModel[], HorizontalGroupedBarChartOptions> {
  protected margin = { top: 20, right: 30, bottom: 20, left: 210 };

  constructor(protected el: ElementRef) {
    super(el);
  }

  protected hasData(): boolean {
    return !!(this.data && this.data.length);
  }

  protected drawChart() {
    const groups = this.data.map(dataEntry => dataEntry.group);
    const subgroups = this.data[0].bars.map(d => d.key);

    const groupsColors = d3.scaleOrdinal()
      .domain(groups)
      .range(this.options.colors);

    const generateGroupColors = (subgroups: string[], color: any) => {
      return subgroups.reduce((initial, subGroup: string, i: number) => {
        return { ...initial, [subGroup]: colorPalette[color.name][color.shade + i * 200] };
      }, {});
    }

    const chartColorPalette = groups.reduce((initial, group) => {
      return { ...initial, [group]: generateGroupColors(subgroups, groupsColors(group)) };
    }, {});

    const maxDataValue = this.getBarsMaxValue();
    const dataAxisStepGap = Math.ceil(maxDataValue / this.options.dataAxisStep);

    // Add X axis
    var x = d3.scaleLinear()
      .domain([0, dataAxisStepGap * this.options.dataAxisStep + dataAxisStepGap])
      .range([0, this.width]);

    // Add ticks
    this.svg.append('g')
      .call(
        d3.axisBottom(x)
          .ticks(this.options.dataAxisStep)
          .tickFormat((d: any) => this.options.tickFormat ? this.options.tickFormat(d) : d)
          // span ticks across the whole chart width
          .tickSizeInner(this.height)
          .tickSizeOuter(0)
          .tickPadding(10))
      .call((g: any) => g.select('.domain').remove())
      .selectAll('text')
      .attr('transform', `translate(0, -${this.height + 30})`);

    this.svg
      .append('text')
      .attr('x', -this.margin.left)
      .attr('y', -10)
      .attr('fill', '#5C5C5C')
      .style('font-size', '1rem')
      .text(() => 'Country');

    this.svg
      .append('line')
      .style("stroke", "black")  // colour the line
      .attr("x1", -this.margin.left)
      .attr("x2", this.width + this.margin.left)

    // Y axis
    var y = d3.scaleBand()
      .range([0, this.height])
      .domain(groups);

    // Add ticks
    const yAxis = d3.axisLeft(y).tickSize(0);
    this.svg.append('g')
      .call(yAxis)
      .call((g: any) => g.select('.domain').remove())
      .selectAll('text')
      .attr('class', (d: any) => d)
      .attr('x', -this.margin.left)
      .style('font-size', '1rem')
      .style('text-anchor', 'start');

    // Another scale for subgroup position
    const ySubgroup: any = d3.scaleBand()
      .domain(subgroups)
      .range([0, y.bandwidth()]) // fixed width of bars
      .padding(0.2);

    var xGridLine = yAxis
      .tickSize(-this.width - this.margin.left)
      .tickFormat(() => '')

    this.svg.append('g')
      .classed('gridLine', true)
      .attr('transform', `translate(${-this.margin.left}, ${y.bandwidth() / 2})`)
      .call(xGridLine)
      .call((g: any) => g.select('.domain').remove());

    //Bars
    this.svg
      .append('g')
      .selectAll('g')
      .data(this.data)
      .enter()
      .append('g')
      .each((data: HorizontalGroupedBarChartDataModel, i: number, nodes: any) => {
        d3.select(nodes[i])
          .attr('class', (d: any) => `group ${d.group}`)
          .attr('transform', (d: any) => `translate(0, ${y(d.group)})`)
          .selectAll('rect')
          .data((d: any) => d.bars)
          .enter()
          .append('text')
          .style('font-size', '0.875rem')
          .attr('class', 'label')
          .attr('fill', '#ADADAD')
          .attr('x', (d: any) => x(d.value) + 10)
          .attr('y', (d: any) => ySubgroup(d.key) + ySubgroup.bandwidth() / 2 + ySubgroup.paddingInner() * 10)
          .text((d: any) => this.options.tickFormat ? this.options.tickFormat(d.value) : d.value)
          .exit()
          .data((d: any) => d.bars)
          .enter()
          .append('rect')
          .attr('class', (d: any) => `subgroup ${d.key}`)
          .attr('x', x(0))
          .attr('y', (d: any) => ySubgroup(d.key) + (ySubgroup.bandwidth() - Math.min(ySubgroup.bandwidth(), 80)) / 2)
          .attr('width', (d: any) => x(d.value))
          .attr('height', Math.min(ySubgroup.bandwidth(), 80))
          .attr('fill', (d: any) => chartColorPalette[data.group][d.key])
          // rounded corners
          .attr('rx', 3)
          .attr('ry', 3);
      });
  }

  protected selectGroup(group: string): void {
    d3.selectAll(`#${this.chartId} text[class='${CSS.escape(group)}']`).style('font-weight', '600');
  }

  private getBarsMaxValue(): number {
    return this.data.reduce((max, dataEntry) => {
      const barsMax = Math.max.apply(Math, dataEntry.bars.map(bar => bar.value));
      if (barsMax > max) {
        max = barsMax;
      }

      return max;
    }, 0);
  }
}
